import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Link from 'next/link'

import { A11y, Keyboard, Navigation } from 'swiper'
import type { Swiper as SwiperInstance } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

import type { RichTextField } from '@prismicio/client'

import linkResolver from '@lib/prismic/link-resolver'
import RichTextSerializer from '@components/RichTextSerializer'
import VideoCard from './VideoCard'

export interface UgcVideoCarouselProps {
	sliceType?: string
	sliceVariation?: string
	title: RichTextField
	description: RichTextField
	ctaLabel: string
	ctaLink: any
	backgroundColor: string
	videos: Array<{
		videoUrl: string
		thumbnailUrl: string
		thumbnailAlt: string
		overlayText: string
	}>
}

export default function UgcVideoCarousel({
	sliceType,
	sliceVariation,
	title,
	description,
	ctaLabel,
	ctaLink,
	backgroundColor,
	videos
}: UgcVideoCarouselProps) {
	const activeVideoRef = useRef<HTMLVideoElement | null>(null)
	const prevButton = useRef<HTMLButtonElement>(null)
	const nextButton = useRef<HTMLButtonElement>(null)
	const [isMounted, setIsMounted] = useState(false)
	const [showNavArrows, setShowNavArrows] = useState(false)

	const count = videos.length

	const ctaResolvedHref = ctaLink ? linkResolver(ctaLink) : ''

	const breakpoints = useMemo(() => {
		const n = count
		// Extra horizontal offset makes Swiper think the track overflows when every slide
		// already fits — that keeps navigation visible. Drop offsets when all rows fit.
		const mdFitsAll = n <= 2
		const lgFitsAll = n <= 4
		return {
			640: {
				slidesPerView: mdFitsAll ? n : Math.min(2.2, n),
				spaceBetween: 16,
				slidesOffsetBefore: mdFitsAll ? 0 : 16,
				slidesOffsetAfter: 0
			},
			1024: {
				// Desktop: show exactly n cards when n ≤ 4; peek only if more than 4.
				slidesPerView: lgFitsAll ? n : 4.2,
				spaceBetween: 16,
				slidesOffsetBefore: lgFitsAll ? 0 : 16,
				slidesOffsetAfter: 0
			}
		}
	}, [count])

	const baseSlidesPerView = count <= 1 ? 1 : 1.4
	const baseOffsetBefore = count <= 1 ? 0 : 12

	const syncNavVisibility = useCallback((swiper: SwiperInstance) => {
		if (!swiper) return
		requestAnimationFrame(() => {
			setShowNavArrows(!swiper.isLocked)
		})
	}, [])

	useEffect(() => {
		setIsMounted(true)
	}, [])

	if (!videos || !videos.length) return null

	return (
		<section
			data-slice-type={sliceType}
			data-slice-variation={sliceVariation}
			className="section py-16 lg:py-20 overflow-hidden"
			style={{ backgroundColor: backgroundColor || '#FFFFFF' }}
			suppressHydrationWarning
		>
			<div className="container">
				<div className="flex flex-col lg:flex-row lg:items-center lg:gap-10">
					{/* Left column — Text content */}
					<div className="w-full lg:w-[280px] xl:w-[320px] lg:shrink-0 mb-8 lg:mb-0 text-center lg:text-left">
						{title?.[0] && (
							<div className="text-3xl lg:text-5xl title mb-4 [&_h2]:text-inherit [&_h2]:font-inherit [&_h2]:leading-inherit">
								<RichTextSerializer content={title} />
							</div>
						)}

						{description?.[0] && (
							<div className="text-base lg:text-lg mb-6">
								<RichTextSerializer content={description} />
							</div>
						)}

						{ctaLabel && ctaLink && (
							<Link legacyBehavior href={ctaResolvedHref}>
								<a className="button button-action inline-block py-3 px-4 text-lg">{ctaLabel}</a>
							</Link>
						)}
					</div>

					{/* Right column — Video cards (horizontal scroll on all breakpoints) */}
					<div className="-ml-4 -mr-1 md:-mx-8 lg:mx-0 lg:-mr-8 lg:min-w-0 lg:flex-1">
						{!isMounted ? null : (
							<div className="relative">
								<div className="lg:overflow-hidden">
									<Swiper
										className="!overflow-visible"
										modules={[A11y, Keyboard, Navigation]}
										navigation={{ nextEl: nextButton.current, prevEl: prevButton.current }}
										keyboard
										watchOverflow
										slidesPerView={baseSlidesPerView}
										spaceBetween={12}
										slidesOffsetBefore={baseOffsetBefore}
										slidesOffsetAfter={0}
										breakpoints={breakpoints}
										onSwiper={(swiper) => {
											// One layout pass so watchOverflow sees final widths (do not hook `update` → avoids recursion).
											requestAnimationFrame(() => {
												swiper.update()
												syncNavVisibility(swiper)
											})
											const onSync = () => syncNavVisibility(swiper)
											swiper.on('breakpoint', onSync)
											swiper.on('resize', onSync)
										}}
										onSlideChange={() => {
											// Stop playback when the user moves the carousel (avoids audio off-screen).
											activeVideoRef.current?.pause()
											activeVideoRef.current = null
										}}
									>
										{videos.map((video, slideIndex) => (
											<SwiperSlide key={`${video.videoUrl}-${slideIndex}`}>
												<VideoCard
													videoUrl={video.videoUrl}
													thumbnailUrl={video.thumbnailUrl}
													thumbnailAlt={video.thumbnailAlt}
													overlayText={video.overlayText}
													activeVideoRef={activeVideoRef}
												/>
											</SwiperSlide>
										))}
									</Swiper>
								</div>
								<button
									type="button"
									ref={prevButton}
									className={
										showNavArrows
											? 'swiper-button swiper-button-prev hidden md:!flex !left-2'
											: 'swiper-button swiper-button-prev hidden'
									}
									aria-label="Previous videos"
								>
									<i className="fa-light fa-chevron-left" aria-hidden="true" />
								</button>
								<button
									type="button"
									ref={nextButton}
									className={
										showNavArrows
											? 'swiper-button swiper-button-next hidden md:!flex !right-2'
											: 'swiper-button swiper-button-next hidden'
									}
									aria-label="Next videos"
								>
									<i className="fa-light fa-chevron-right" aria-hidden="true" />
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</section>
	)
}
